import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Address, AddressHelper } from "@app/address/model/address";
import { APN_LIST, CellularDevice } from "@app/cellular/models/cellular-device.model";
import { checksumValid, iccidValid } from "@app/shared/validators";
import { distinctUntilChanged, skip, takeUntil } from "rxjs/operators";
import { FormSubmissionError, FormSubmissionStatus } from "../cellular-info/cellular-info.component";

import { TitleCasePipe } from "@angular/common";
import { CellularService } from "@app/cellular/services/cellular.service";
import { Project } from "@app/project/model/project";
import { SecuritySystem } from "@app/project/model/security-system";
import { CellularProviderType } from "@app/shared/databases/cellular.database.service";
import { MacInpDirective } from "@app/shared/directives/mac-inp.directive";
import { NgChanges } from "@app/shared/model/ng-changes.model";
import { macAddressValid } from "@app/shared/validators/macAddressValid";
import { Subject } from "rxjs";
import { AppHelperService } from "../../../shared/services/app-helper.service";

/**
 * Cosmetic options for CellularEditComponent
 */
export interface CellularEditConfig {
    /** Whether the rows (inputs / buttons) should have width: 100%. Default false. */
    fullWidth: boolean;
    /** Whether to hide the button when submissionStatus.inProgress is true. Default false. */
    hideSubmit: boolean;
    /** Whether to hide the Edit Cellular Provider title. Default false. */
    hideTitle: boolean;
    /** Whether to title-case the button text. Default false. */
    ignoreTitleCase: boolean;
    /** Escape hatch for misc padding issues. Default false. */
    isInWizard: boolean;
    /** Whether the spinner and its caption should be left-aligned instead of centered. Default false. */
    leftAlignSpinner: boolean;
}

const DEFAULT_CONFIG: Readonly<CellularEditConfig> = {
    fullWidth: false,
    hideSubmit: false,
    hideTitle: false,
    ignoreTitleCase: false,
    leftAlignSpinner: false,
    isInWizard: false,
};

@Component({
    selector: "clare-cellular-edit",
    templateUrl: "./cellular-edit.component.html",
    styleUrls: ["./cellular-edit.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CellularEditComponent implements OnInit, OnChanges, OnDestroy {
    @Input() address: Address;
    @Input() cellProviderOnPanel: CellularProviderType;
    @Input() chDeviceIccid: string;
    @Input() dbSecurityDevice: SecuritySystem;
    @Input() device: CellularDevice;
    @Input() project: Project;
    @Input() submissionStatus: FormSubmissionStatus;

    @Input() cellularProviderAction = "activate";
    @Input() config = DEFAULT_CONFIG;
    @Input() defaultProviderOverride?: CellularProviderType;
    @Input() disableSubmit = false;
    @Input() errors: Partial<Record<CellularProviderType, FormSubmissionError>> = {};
    @Input() isCellActive = false;
    @Input() isIccidMismatch = false;
    @Input() isProjectOnline = true;

    @Output() updateProvider = new EventEmitter<string>();
    @Output() updateProviderAction = new EventEmitter<{ shouldClear: boolean; provider: CellularProviderType }>();
    @Output() providerSelection = new EventEmitter<string>();
    @Output() triggerCellularAction = new EventEmitter<{ action: string; cellularForm?: Record<string, any> }>();

    public cellularForm: FormGroup = this.formBuilder.group({
        iccid: ["", [Validators.required, iccidValid, checksumValid]],
        imei: [""],
        mdnZipCode: ["", Validators.minLength(5)],
        provider: ["", [Validators.required]],
        macAddress: ["", [Validators.required, macAddressValid]],
    });
    public isCellCarrierMismatch = false;
    public providerList = APN_LIST;

    private readonly DEFAULT_PROVIDER_VALUE = CellularProviderType.NONE;
    private readonly COUNTRY_CODES = {
        trinidad: "Trinidad and Tobago",
        canada: "Canada",
        usa: "United States",
    };
    private readonly unsubscribe$ = new Subject();

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly cellularService: CellularService,
        private readonly titleCasePipe: TitleCasePipe
    ) {}

    /**
     * @description getter to retrive the form controls
     * @readonly
     * @returns {Record<string, AbstractControl>} the form controls
     */
    get fields(): Record<string, AbstractControl> {
        return this.cellularForm.controls;
    }

    ngOnChanges(changes: NgChanges<CellularEditComponent>) {
        if (changes.cellProviderOnPanel) {
            const newProviderValue: CellularProviderType = changes.cellProviderOnPanel.currentValue;
            if (newProviderValue) {
                this.setCarrierList(newProviderValue);
                this.checkAndSetCarrierError(this.cellularForm.value.provider, newProviderValue);
            }
        }

        if (changes.device?.currentValue) {
            this.cellularForm.patchValue({
                imei: changes.device.currentValue.imei || this.dbSecurityDevice?.imei,
                iccid: changes.device.currentValue.iccid,
            });
        }

        if (changes.dbSecurityDevice?.currentValue) {
            this.updateMacAddressField(changes.dbSecurityDevice.currentValue);
        }

        if (changes.address?.currentValue) {
            this.updateZipCode(changes.address.currentValue.zipcode);
        }

        // Reset provider field to new default
        if (changes.defaultProviderOverride?.currentValue) {
            this.setCarrierList(this.cellProviderOnPanel);
        }
    }

    private updateZipCode(rawZipCode = "") {
        if (AppHelperService.isUSZipcode(rawZipCode)) {
            this.fields.mdnZipCode.setValue(rawZipCode.substring(0, 5));
        } else {
            this.fields.mdnZipCode.setValue(rawZipCode);
        }
    }

    ngOnInit() {
        this.setCarrierList(this.cellProviderOnPanel || CellularProviderType.NONE);

        this.cellularForm.controls.provider.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((providerFormControlValue: CellularProviderType) => {
                const isNoneModifying =
                    (providerFormControlValue !== CellularProviderType.NONE && this.cellProviderOnPanel === CellularProviderType.NONE) ||
                    (providerFormControlValue === CellularProviderType.NONE && this.cellProviderOnPanel !== CellularProviderType.NONE);
                if (!!this.cellProviderOnPanel && !isNoneModifying) {
                    this.checkAndSetCarrierError(providerFormControlValue, this.cellProviderOnPanel || this.DEFAULT_PROVIDER_VALUE);
                }

                this.providerSelection.emit(providerFormControlValue);
            });

        this.cellularForm.valueChanges
            .pipe(
                takeUntil(this.unsubscribe$),
                distinctUntilChanged((previous, current) => previous.provider === current.provider && previous.iccid === current.iccid),
                skip(1)
            )
            .subscribe(formControlValue => {
                const providerFormControlValue = formControlValue.provider;
                // If the db device does not have a provider, we want the action to be saved, so send null as the provider.
                this.updateProviderAction.emit({
                    shouldClear: true,
                    provider: !this.dbSecurityDevice?.cellularProvider ? null : providerFormControlValue,
                });
            });
    }

    updateMacAddressField(device: SecuritySystem) {
        if (device) {
            device.macAddress = MacInpDirective.uppercaseValue(device.macAddress);
            this.fields.macAddress.setValue(device.macAddress);
        }
    }

    /**
     * @description Handles a form action to perform with the 3rd party provider.
     * Can activate a device, check its status or deactivate a device.
     * @param {string} action the cellular device action to perform
     * @param {FormGroup} cellularForm the form containing the updated values
     */
    public callProviderWithAction(action: string, cellularForm: FormGroup) {
        if (cellularForm.valid) {
            this.triggerCellularAction.emit({ action, cellularForm: cellularForm.value });
        } else {
            this.cellularService.displayError("The form is invalid. Please check for errors.");
        }
    }

    /**
     * @description Determine if the panel iccid matches the iccid used in the cellular device being edited
     * @readonly
     * @returns {boolean} does the panel iccid match the cellular device iccid
     */
    get isVerifiedPanelIccid(): boolean {
        return !!this.device?.iccid && this.device.iccid === this.chDeviceIccid;
    }

    get coverageMapUrl(): string {
        const { provider } = this.cellularForm.value as { provider: CellularProviderType };
        let url;
        if (!this.isCellActive) {
            if (provider === CellularProviderType.VERIZON) {
                url = "https://www.verizon.com/coverage-map/";
            } else if (provider === CellularProviderType.ATT) {
                url = "https://www.att.com/maps/wireless-coverage.html";
            }
        }
        return url;
    }

    /**
     * @description Disables the submit button based on whether we've submitted the form already and awaiting a repsonse,
     * or if there is a mismatch in the ICCIDs (determined in the cellular-info component)
     * @readonly
     * @returns {boolean} whether or not the submit button should be disabled
     */
    get shouldDisableSubmit(): boolean {
        return (
            !this.cellularForm.valid ||
            this.disableSubmit ||
            this.submissionStatus.inProgress ||
            (this.isIccidMismatch && !this.isCellActive)
        );
    }

    /**
     * @description Does the project's country use postal codes.
     * Used to show or hide the postal/ZIP code field in the form.
     * @returns {boolean} Whether the country uses postal codes
     */
    get hasPostal(): boolean {
        return AddressHelper.hasPostalCodes(this.address?.country);
    }

    get buttonText(): string {
        let result = this.cellularProviderAction;

        if (this.submissionStatus.inProgress && this.submissionStatus.buttonText) {
            result = this.submissionStatus.buttonText;
        }

        if (!this.config.ignoreTitleCase) {
            result = this.titleCasePipe.transform(result);
        }

        return result;
    }

    /**
     * @description Determine which providers should be shown based on what we get back for a provider in the ClareOne panel.
     * If the panel is a Verizon panel, we only show the Verizon option.
     * Otherwise, we display all values except Verizon.
     * @param {(CellularProviderType)} carrierValueFromPanel The cellular provider currently selected in the ClareOne panel
     */
    private setCarrierList(carrierValueFromPanel: CellularProviderType) {
        if (carrierValueFromPanel && carrierValueFromPanel !== this.DEFAULT_PROVIDER_VALUE) {
            if (carrierValueFromPanel === CellularProviderType.VERIZON) {
                // Hide all selections except verizon
                this.providerList = this.providerList.filter(
                    provider => provider.type === CellularProviderType.VERIZON || provider.type === CellularProviderType.NONE
                );
            } else {
                // Hide verizon
                this.providerList = this.providerList.filter(provider => provider.type !== CellularProviderType.VERIZON);
            }
        } else {
            this.providerList = APN_LIST;
        }

        if (this.providerList.some(provider => provider.type === this.defaultProviderOverride)) {
            this.cellularForm.controls.provider.setValue(this.defaultProviderOverride);
        } else {
            this.autoselectProvider(carrierValueFromPanel);
        }
    }

    /**
     * @description Determine if a provider value should be selected. The following criteria is used:
     * 1. By default, the panel value is used.
     * 2. If there is a provider saved in the database, that value is used.
     * 3. Otherwise, based on the project's country, we select an appropriate carrier.
     * @param {CellularProviderType} providerFromPanel The provider fmor the ClareOne panel
     */
    private autoselectProvider(providerFromPanel: CellularProviderType) {
        // Set to provider that is received from panel. Will get overwritten if db has value
        let providerToSelect = providerFromPanel;
        if (this.dbSecurityDevice?.cellularProvider) {
            // Db takes priority. If db value exists, set provider to db value.
            providerToSelect = this.dbSecurityDevice.cellularProvider;
        } else if (!providerToSelect) {
            // If no db value, AND no provider received from panel, select based on country.
            switch (this.address.country) {
                case this.COUNTRY_CODES.usa: {
                    providerToSelect = CellularProviderType.VERIZON;
                    break;
                }
                case this.COUNTRY_CODES.canada: {
                    providerToSelect = CellularProviderType.ATT;
                    break;
                }
                case this.COUNTRY_CODES.trinidad: {
                    providerToSelect = CellularProviderType.BMOBILE;
                    break;
                }
                default: {
                    providerToSelect = CellularProviderType.NONE;
                }
            }
        }

        setTimeout(() => {
            this.cellularForm.controls.provider.setValue(providerToSelect);
        }, 1);
    }

    /**
     * @description Checks whether the provider values are equal and sets form errors accordingly
     * @param {string} provider1 One of the provider values to check
     * @param {string} provider2 The other provider value to check
     */
    private checkAndSetCarrierError(provider1: string, provider2: string) {
        const isProviderMismatch = provider1.toLowerCase() !== provider2.toLowerCase();
        if (isProviderMismatch) {
            this.fields.provider.markAsTouched();
            this.fields.provider.markAsDirty();
            this.fields.provider.setErrors({ invalid: "Carrier Mismatch" });
            this.isCellCarrierMismatch = true;
        } else {
            if (this.fields.provider.errors) {
                this.fields.provider.setErrors(null);
            }
            this.isCellCarrierMismatch = false;
        }
    }

    /**
     * @description Emit the new provider vaue to the parent component to be updated in the device
     */
    public updateCarrierDCOProp() {
        // TODO: Live with this unsafe type issue until stronly typed Angular forms are released (possibly Angular 14?)
        this.updateProvider.emit(this.fields.provider.value.toUpperCase());
    }

    get error() {
        return this.errors[this.fields.provider.value as CellularProviderType];
    }

    get updateProviderError(): string {
        if (!this.error?.isUpdateProviderError) {
            return "";
        }

        return this.error.text;
    }

    get shouldShowUpdateProviderError(): boolean {
        const isActivationIncomplete = !this.dbSecurityDevice?.isCompleted;
        return (isActivationIncomplete || this.isCellCarrierMismatch) && this.isCellActive && !this.submissionStatus.inProgress;
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }
}
